/**
 * TilesetCatalog — Registry of known tilesets with attribution and purchase info.
 *
 * TileForge ships .tilemeta.json files (tile intelligence) for popular tilesets
 * but NOT the tileset images themselves. When a user opens a map that references
 * a tileset they don't have, TileForge shows the purchase link.
 *
 * This catalog serves three purposes:
 *   1. Auto-detect tilesets when users import images (by filename/dimensions)
 *   2. Provide purchase links for missing tilesets
 *   3. Drive the "Recommended Tilesets" section in the UI
 *
 * Tileset artists benefit: every generated map is an advertisement for their work.
 * Users benefit: instant generator intelligence without manual tagging.
 *
 * itch.io Partner & Affiliate program (https://itch.io/partners):
 *   - Partner program exists (verification, partner library, affiliate codes)
 *   - Revenue sharing for affiliates is NOT yet active (as of mid-2025, still in development)
 *   - Apply anyway to get positioned for when it launches
 *   - In the meantime: use UTM-tagged links (?utm_source=tileforge&utm_medium=mapgen)
 *   - Creators can enable "co-op revenue sharing" per-page — reach out to artists directly
 *   - Create a TileForge Collection on itch.io to curate compatible tilesets (free, drives traffic)
 *   - TODO: Apply for partner program, contact TheLazyStone/LimeZu about co-op rev share
 */

/**
 * @typedef {Object} CatalogEntry
 * @property {string} id              — Unique identifier (matches .tilemeta.json filename)
 * @property {string} name            — Display name
 * @property {string} artist          — Creator/studio
 * @property {string} purchaseUrl     — Where to buy (itch.io, etc.)
 * @property {string} [website]       — Artist homepage
 * @property {string} licenseType     — 'commercial', 'cc-by-4.0', 'free', 'cc0'
 * @property {string} [licenseUrl]    — Full license terms URL
 * @property {number} tileSize        — Pixel size (16, 32, etc.)
 * @property {number} columns         — Tileset columns
 * @property {number} tileCount       — Total tiles
 * @property {string[]} [filenames]   — Known filenames for auto-detection
 * @property {string[]} categories    — ['interior', 'exterior', 'terrain', 'characters', 'ui']
 * @property {string[]} tags          — ['modern', 'fantasy', 'sci-fi', 'top-down', 'rpg']
 * @property {boolean} hasMetaFile    — Whether we ship a .tilemeta.json for this tileset
 * @property {string} [metaUrl]       — URL to download the .tilemeta.json if hosted remotely
 * @property {string} [notes]         — Additional info
 */

export const TILESET_CATALOG = [
  // ─── LimeZu ────────────────────────────────────────────────────
  {
    id: 'modern-interiors',
    name: 'Modern Interiors',
    artist: 'LimeZu',
    purchaseUrl: 'https://limezu.itch.io/moderninteriors?utm_source=tileforge&utm_medium=mapgen',
    website: 'https://limezu.itch.io',
    licenseType: 'commercial',
    tileSize: 16,
    columns: 0,    // varies by sheet
    tileCount: 0,  // varies by sheet
    filenames: ['modern_interiors', 'moderninteriors', 'MI_'],
    categories: ['interior'],
    tags: ['modern', 'top-down', 'furniture', 'rooms', '16px'],
    hasMetaFile: false, // TODO: author this
    notes: 'Most popular modern interior tileset. Rooms, furniture, appliances, bathroom, kitchen, office.',
  },
  {
    id: 'modern-exteriors',
    name: 'Modern Exteriors',
    artist: 'LimeZu',
    purchaseUrl: 'https://limezu.itch.io/modernexteriors?utm_source=tileforge&utm_medium=mapgen',
    website: 'https://limezu.itch.io',
    licenseType: 'commercial',
    tileSize: 16,
    columns: 0,
    tileCount: 0,
    filenames: ['modern_exteriors', 'modernexteriors', 'ME_'],
    categories: ['exterior', 'terrain'],
    tags: ['modern', 'top-down', 'buildings', 'roads', 'vehicles', '16px'],
    hasMetaFile: false,
    notes: 'Companion to Modern Interiors. Roads, sidewalks, buildings, cars, trees, suburban environments.',
  },
  {
    id: 'serene-village',
    name: 'Serene Village',
    artist: 'LimeZu',
    purchaseUrl: 'https://limezu.itch.io/serenevillage?utm_source=tileforge&utm_medium=mapgen',
    website: 'https://limezu.itch.io',
    licenseType: 'commercial',
    tileSize: 16,
    columns: 0,
    tileCount: 0,
    filenames: ['serene_village', 'serenevillage'],
    categories: ['exterior', 'terrain', 'interior'],
    tags: ['fantasy', 'medieval', 'village', 'top-down', 'rpg', '16px'],
    hasMetaFile: false,
    notes: 'Fantasy village tileset. Great for RPG towns and overworld.',
  },

  // ─── Cainos ────────────────────────────────────────────────────
  {
    id: 'cute-fantasy-rpg',
    name: 'Cute Fantasy RPG',
    artist: 'Cainos',
    purchaseUrl: 'https://cainos.itch.io/pixel-art-top-down-basic?utm_source=tileforge&utm_medium=mapgen',
    website: 'https://cainos.itch.io',
    licenseType: 'commercial',
    tileSize: 16,
    columns: 0,
    tileCount: 0,
    filenames: ['cute_fantasy', 'cainos'],
    categories: ['terrain', 'exterior'],
    tags: ['fantasy', 'rpg', 'top-down', 'autotile', 'wang', '16px'],
    hasMetaFile: false,
    notes: 'Clean autotile terrain set with Wang/blob variants. Good reference for autotile tagging.',
  },

  // ─── Szadi Art ──────────────────────────────────────────────────
  {
    id: 'rogue-fantasy-catacombs',
    name: 'Rogue Fantasy Catacombs',
    artist: 'Szadi Art',
    purchaseUrl: 'https://szadiart.itch.io/rogue-fantasy-catacombs?utm_source=tileforge&utm_medium=mapgen',
    website: 'https://szadiart.itch.io',
    licenseType: 'commercial',
    tileSize: 16,
    columns: 0,
    tileCount: 0,
    filenames: ['catacombs', 'rogue_fantasy'],
    categories: ['interior', 'terrain'],
    tags: ['fantasy', 'dungeon', 'dark', 'rpg', '16px'],
    hasMetaFile: false,
    notes: 'Dungeon/catacomb tileset. Walls, floors, traps, furniture, enemies.',
  },

  // ─── TheLazyStone ────────────────────────────────────────────────
  {
    id: 'post-apocalypse',
    name: 'Post-Apocalypse Pixel Art Asset Pack',
    artist: 'TheLazyStone',
    purchaseUrl: 'https://thelazystone.itch.io/post-apocalypse-pixel-art-asset-pack?utm_source=tileforge&utm_medium=mapgen',
    website: 'https://thelazystone.itch.io',
    licenseType: 'commercial',
    tileSize: 16,
    columns: 0,
    tileCount: 3300,
    filenames: ['post_apocalypse', 'postapocalypse', 'lazystone', 'building_tiles'],
    categories: ['exterior', 'terrain', 'interior', 'characters', 'ui'],
    tags: ['zombie', 'post-apocalypse', 'survival', 'top-down', 'modern', '16px'],
    hasMetaFile: true,
    notes: 'Used by One More Night. 3300+ sprites: characters, zombies, weapons, buildings (4 color sets), vehicles, props, UI. Commercial use requires $2+ purchase. No redistribution.',
  },

  // ─── Kenney ────────────────────────────────────────────────────
  {
    id: 'kenney-tiny-town',
    name: 'Tiny Town',
    artist: 'Kenney',
    purchaseUrl: 'https://kenney.nl/assets/tiny-town?utm_source=tileforge&utm_medium=mapgen',
    website: 'https://kenney.nl',
    licenseType: 'cc0',
    tileSize: 16,
    columns: 0,
    tileCount: 0,
    filenames: ['tiny_town', 'kenney_town'],
    categories: ['exterior', 'terrain'],
    tags: ['modern', 'top-down', 'town', 'free', '16px'],
    hasMetaFile: false,
    notes: 'Free CC0 tileset. Good for testing and demos. No purchase required.',
  },
  {
    id: 'kenney-tiny-dungeon',
    name: 'Tiny Dungeon',
    artist: 'Kenney',
    purchaseUrl: 'https://kenney.nl/assets/tiny-dungeon?utm_source=tileforge&utm_medium=mapgen',
    website: 'https://kenney.nl',
    licenseType: 'cc0',
    tileSize: 16,
    columns: 0,
    tileCount: 0,
    filenames: ['tiny_dungeon', 'kenney_dungeon'],
    categories: ['interior', 'terrain'],
    tags: ['fantasy', 'dungeon', 'free', '16px'],
    hasMetaFile: false,
    notes: 'Free CC0 dungeon tileset. Good for testing and demos.',
  },
];

export class TilesetCatalog {
  constructor(entries = TILESET_CATALOG) {
    this._entries = new Map();
    this._filenameIndex = new Map(); // lowercase filename fragment → entry id
    for (const entry of entries) {
      this._entries.set(entry.id, entry);
      for (const fn of (entry.filenames || [])) {
        this._filenameIndex.set(fn.toLowerCase(), entry.id);
      }
    }
  }

  /** Get a catalog entry by ID. */
  get(id) {
    return this._entries.get(id) || null;
  }

  /** List all entries. */
  list() {
    return [...this._entries.values()];
  }

  /** Filter by category. */
  byCategory(category) {
    return this.list().filter(e => e.categories.includes(category));
  }

  /** Filter by tag. */
  byTag(tag) {
    return this.list().filter(e => e.tags.includes(tag));
  }

  /** Filter to only free tilesets. */
  free() {
    return this.list().filter(e => e.licenseType === 'cc0' || e.licenseType === 'free');
  }

  /** Filter to tilesets we have .tilemeta.json files for. */
  withMeta() {
    return this.list().filter(e => e.hasMetaFile);
  }

  /**
   * Try to auto-detect a tileset from an imported filename.
   * Matches against filename stem only (strips path and extension).
   * Requires fragment to appear as a word boundary match, not arbitrary substring.
   * @param {string} filename — e.g. 'Modern_Interiors_16x16.png' or 'C:/art/MI_tiles.png'
   * @returns {CatalogEntry|null}
   */
  detectFromFilename(filename) {
    // Extract stem: strip path separators and extension
    const stem = filename.replace(/.*[/\\]/, '').replace(/\.[^.]+$/, '').toLowerCase();
    for (const [fragment, id] of this._filenameIndex) {
      // Match as prefix, full stem, or delimited segment (_, -, space boundaries)
      if (stem === fragment || stem.startsWith(fragment + '_') || stem.startsWith(fragment + '-') ||
          stem.startsWith(fragment + ' ') || stem.includes('_' + fragment) ||
          stem.includes('-' + fragment) || stem.startsWith(fragment)) {
        return this._entries.get(id);
      }
    }
    return null;
  }

  /**
   * Get attribution object suitable for TileMeta from a catalog entry.
   * @param {string} id — Catalog entry ID
   * @returns {Object|null}
   */
  getAttribution(id) {
    const entry = this._entries.get(id);
    if (!entry) return null;
    return {
      artist: entry.artist,
      purchaseUrl: entry.purchaseUrl,
      licenseType: entry.licenseType,
      licenseUrl: entry.licenseUrl || null,
      website: entry.website || null,
      notes: entry.notes || null,
    };
  }

  /** Add a custom entry (for user-registered tilesets). */
  register(entry) {
    this._entries.set(entry.id, entry);
    for (const fn of (entry.filenames || [])) {
      this._filenameIndex.set(fn.toLowerCase(), entry.id);
    }
  }
}
